/**
 * Screenshot Attachment Indicator Component
 *
 * Displays screenshot thumbnails attached to activity log entries (notes).
 * Shows in activity timeline to indicate notes with visual evidence.
 *
 * Module: 3.0 - System Operations (Issues Manager Enhancement)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.2
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { IssueAttachment } from "@shared/schema";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ImageIcon } from "lucide-react";
import { format } from "date-fns";

interface ScreenshotAttachmentIndicatorProps {
  issueId: string;
  activityLogId: string;
}

export function ScreenshotAttachmentIndicator({
  issueId,
  activityLogId,
}: ScreenshotAttachmentIndicatorProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] = useState<IssueAttachment | null>(null);

  // Fetch all screenshots for this issue (uses default queryFn with auth)
  const { data } = useQuery<{ screenshots: IssueAttachment[] }>({
    queryKey: ["/api/crm/issues", issueId, "screenshots"],
    enabled: !!issueId,
  });

  // Filter screenshots linked to this specific note (activity log entry)
  const attachedScreenshots = (data?.screenshots || []).filter((screenshot) => {
    try {
      const metadata = screenshot.sourceMetadata ? JSON.parse(screenshot.sourceMetadata) : {};
      return metadata.noteId === activityLogId;
    } catch {
      return false;
    }
  });

  if (attachedScreenshots.length === 0) return null;

  const handleThumbnailClick = (screenshot: IssueAttachment) => {
    setSelectedScreenshot(screenshot);
    setLightboxOpen(true);
  };

  return (
    <>
      <div className="flex items-center gap-2 mt-2 p-2 bg-muted/30 rounded-md border">
        <ImageIcon className="w-3 h-3 text-muted-foreground flex-shrink-0" />
        <p className="text-xs text-muted-foreground">
          {attachedScreenshots.length} screenshot{attachedScreenshots.length !== 1 ? "s" : ""} attached
        </p>
        <div className="flex gap-1 flex-wrap">
          {attachedScreenshots.map((screenshot) => (
            <div
              key={screenshot.id}
              className="w-8 h-8 rounded border overflow-hidden cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              onClick={() => handleThumbnailClick(screenshot)}
              title={screenshot.fileName}
            >
              <img
                src={`/${screenshot.filePath}`}
                alt={screenshot.fileName}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Preview */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent
          className="max-w-4xl max-h-[90vh] overflow-hidden"
          aria-describedby="note-screenshot-description"
        >
          <DialogTitle className="sr-only">
            Note Screenshot: {selectedScreenshot?.fileName || "Screenshot"}
          </DialogTitle>
          <DialogDescription id="note-screenshot-description" className="sr-only">
            Screenshot attached to note, uploaded on{" "}
            {selectedScreenshot ? format(new Date(selectedScreenshot.createdAt), "MMM d, yyyy HH:mm") : ""}
          </DialogDescription>
          {selectedScreenshot && (
            <div className="space-y-4">
              {/* Screenshot Image */}
              <div className="flex items-center justify-center bg-muted rounded-md">
                <img
                  src={`/${selectedScreenshot.filePath}`}
                  alt={selectedScreenshot.fileName}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>

              {/* Screenshot Metadata */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" />
                  <p className="font-medium text-sm">{selectedScreenshot.fileName}</p>
                </div>

                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  <div>
                    <p className="font-semibold">Uploaded</p>
                    <p>{format(new Date(selectedScreenshot.createdAt), "MMM d, yyyy HH:mm")}</p>
                  </div>
                  <div>
                    <p className="font-semibold">Size</p>
                    <p>{(selectedScreenshot.fileSize / 1024).toFixed(2)} KB</p>
                  </div>
                  {selectedScreenshot.description && (
                    <div className="col-span-2">
                      <p className="font-semibold">Description</p>
                      <p>{selectedScreenshot.description}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
